const MAIN_OPTION = {
  city: 'Select a city',
  parish: 'Select a parish',
  state: 'Select a state',
  serviceArea: 'Select a service area',
  specialty: 'Select a specialty',
};

type ListResponse = string[];
type OmnetProvidersResponse = OmnetProvider[][];

interface OmnetProvider {
  VendorCount: number;
  Rank: number;
  VendorSequence: number;
  VendorNumber: number;
  ChildVendorNumber: number;
  Name: string;
  PreviousName: string;
  BaseVendor: string;
  ServiceArea: string;
  CodeDescription: string;
  VendorContactName: string;
  VendorAddress: VendorAddress;
  VendorPhones: VendorPhone[];
}

interface VendorAddress {
  AddressTypeCode: null;
  Address1: string;
  Address2: string;
  City: string;
  StateCode: string;
  ZipCode: string;
}

interface VendorPhone {
  ContactName: null;
  ContactTypeCode: string;
  ContactValue: string;
}

export default class OmNetSearch {
  private readonly formOmNetSearch;
  private readonly nameInput;
  private readonly citySelection;
  private readonly parishSelection;
  private readonly stateSelection;
  private readonly serviceSelection;
  private readonly specialtySelection;
  private readonly cityUrl = 'https://rest.us.synatic.com/lwccfscdev/webflow-api/GetCityList'; //return list of cities
  private readonly parishUrl = 'https://rest.us.synatic.com/lwccfscdev/webflow-api/GetParishList'; //returns list of parishes
  private readonly stateUrl = 'https://rest.us.synatic.com/lwccfscdev/webflow-api/GetStateList'; //return list of states
  private readonly serviceAreaUrl = 'https://rest.us.synatic.com/lwccfscdev/webflow-api/GetServiceAreaList'; //return list of service areas
  private readonly specialtyUrl = 'https://rest.us.synatic.com/lwccfscdev/webflow-api/GetSpecialtyList'; //return list of secialties
  private readonly omnetProvidersUrl = 'https://rest.us.synatic.com/lwccfscuat/webflow-api/GetOmnetProviders';
  private readonly waiterWrapper;
  private readonly omNetResultWrapper;
  private readonly omNetNoResultWrapper;
  private readonly omNetResultItemTemplate;
  private readonly resetButton;

  constructor() {
    this.formOmNetSearch = document.querySelector('.omnet-form') as HTMLFormElement;
    this.nameInput = this.formOmNetSearch.querySelector(`[omnet-search="name"]`) as HTMLInputElement;
    this.citySelection = this.formOmNetSearch.querySelector(`[omnet-search="city"]`) as HTMLSelectElement;
    this.parishSelection = this.formOmNetSearch.querySelector(`[omnet-search="parish"]`) as HTMLSelectElement;
    this.stateSelection = this.formOmNetSearch.querySelector(`[omnet-search="state"]`) as HTMLSelectElement;
    this.serviceSelection = this.formOmNetSearch.querySelector(`[omnet-search="service-area"]`) as HTMLSelectElement;
    this.specialtySelection = this.formOmNetSearch.querySelector(`[omnet-search="specialty"]`) as HTMLSelectElement;

    this.waiterWrapper = document.querySelector('.omnet-waiting-wrapper') as HTMLElement;

    this.omNetResultWrapper = document.querySelector('.omnet-results-wrapper') as HTMLElement;
    this.omNetResultItemTemplate = this.omNetResultWrapper.querySelector('.find-omnet-results-item') as HTMLElement;
    this.omNetResultItemTemplate.remove();
    this.omNetNoResultWrapper = document.querySelector('.omnet-no-results-wrapper') as HTMLElement;

    //Reset Button
    this.resetButton = this.formOmNetSearch.querySelector('#reset') as HTMLElement;

    // Call removeAllSectionOptions
    this.removeAllSectionOptions(this.citySelection, MAIN_OPTION.city);
    this.removeAllSectionOptions(this.parishSelection, MAIN_OPTION.parish);
    this.removeAllSectionOptions(this.stateSelection, MAIN_OPTION.state);
    this.removeAllSectionOptions(this.serviceSelection, MAIN_OPTION.serviceArea);
    this.removeAllSectionOptions(this.specialtySelection, MAIN_OPTION.specialty);

    // Listen to Event
    this.listener();
  }

  private removeAllSectionOptions(selection: HTMLSelectElement, firstOption: string) {
    // remove all dummy options
    Array.prototype.slice.call(selection.options).forEach((opt) => {
      opt.remove();
    });
    //Add the first option
    const newOption = document.createElement('option') as HTMLOptionElement;
    newOption.textContent = firstOption;
    selection.appendChild(newOption);
  }

  private listener() {
    // Fetch & render the data options for city
    this.fetchData(this.cityUrl, this.citySelection);
    // Fetch & render the data options for parish
    this.fetchData(this.parishUrl, this.parishSelection);
    // Fetch & render the data options for state
    this.fetchData(this.stateUrl, this.stateSelection);
    // Fetch & render the data options for service area selection
    this.fetchData(this.serviceAreaUrl, this.serviceSelection);
    // Fetch & render the data options for specialty
    this.fetchData(this.specialtyUrl, this.specialtySelection);

    // submit listener event for search form.
    this.formOmNetSearch.addEventListener('submit', (e) => {
      e.stopImmediatePropagation();
      e.preventDefault();
      e.stopPropagation();

      // Get all selection values
      let nameValue = '';
      let citySelectValue = '';
      let parishSelectValue = '';
      let stateSelectValue = '';
      let serviceAreaSelectValue = '';
      let specialtySelectValue = '';
      if (this.nameInput.value) nameValue = this.nameInput.value;
      if (this.citySelection.value !== MAIN_OPTION.city) citySelectValue = this.citySelection.value;
      if (this.parishSelection.value !== MAIN_OPTION.parish) parishSelectValue = this.parishSelection.value;
      if (this.stateSelection.value !== MAIN_OPTION.state) stateSelectValue = this.stateSelection.value;
      if (this.serviceSelection.value !== MAIN_OPTION.serviceArea) serviceAreaSelectValue = this.serviceSelection.value;
      if (this.specialtySelection.value !== MAIN_OPTION.specialty) specialtySelectValue = this.specialtySelection.value;

      // Make serach query base on available form values
      if (
        nameValue ||
        citySelectValue ||
        parishSelectValue ||
        stateSelectValue ||
        serviceAreaSelectValue ||
        specialtySelectValue
      ) {
        // Show waiter
        this.waiterWrapper.style.display = 'block';

        // Hide Search result wrapper
        this.omNetResultWrapper.style.display = 'none';

        // Hide the No result wrapper
        this.omNetNoResultWrapper.style.display = 'none';

        // remove all previous search result
        Array.prototype.slice.call(this.omNetResultWrapper.children).forEach((res) => {
          res.remove();
        });

        // Query Value
        this.searchQuery(
          nameValue,
          citySelectValue,
          parishSelectValue,
          stateSelectValue,
          serviceAreaSelectValue,
          specialtySelectValue
        );
      }
    });

    //Reset Search Form on reset button click
    this.resetButton.addEventListener('click', () => {
      this.nameInput.value = '';
      this.citySelection.options[0].selected = true;
      this.parishSelection.options[0].selected = true;
      this.stateSelection.options[0].selected = true;
      this.serviceSelection.options[0].selected = true;
      this.specialtySelection.options[0].selected = true;

      // remove all previous search result
      Array.prototype.slice.call(this.omNetResultWrapper.children).forEach((res) => {
        res.remove();
      });

      // Hide the No result wrapper
      this.omNetNoResultWrapper.style.display = 'none';
    });
  }

  private async fetchData(endpoint: string, selection: HTMLSelectElement) {
    try {
      const response = await fetch(endpoint);
      const data: ListResponse = await response.json();

      data.forEach((opt) => {
        const newOption = document.createElement('option') as HTMLOptionElement;
        newOption.value = opt;
        newOption.textContent = opt;
        selection.appendChild(newOption);
      });
    } catch (err) {
      // Hide The waiting text wrapper while fetching agent result
    }
  }

  private async searchQuery(name = '', city = '', parish = '', state = '', serviceArea = '', specialty = '') {
    const url = new URL(this.omnetProvidersUrl);
    url.searchParams.append('providerName', name);
    url.searchParams.append('parish', parish);
    url.searchParams.append('city', city);
    url.searchParams.append('state', state);
    url.searchParams.append('serviceArea', serviceArea);
    url.searchParams.append('specialty', specialty);

    const endpoint = url.toString();

    try {
      const response = await fetch(endpoint);
      const data: OmnetProvidersResponse = await response.json();

      const omnetProviders = data.flat();

      // Hide waiter
      this.waiterWrapper.style.display = 'none';
      // Display Result wrapper
      this.omNetResultWrapper.style.display = 'block';

      if (omnetProviders && omnetProviders.length) {
        // Render omnetProviders to DOM
        omnetProviders.forEach((result) => {
          // Render result to template
          this.renderResultToDOMTemplate(result);
        });
      } else {
        // Display the No result wrapper
        this.omNetNoResultWrapper.style.display = 'block';
      }
    } catch (err) {
      // Hide The waiting text wrapper after fetching OMNet query result
      this.waiterWrapper.style.display = 'none';
      // Display the No result wrapper
      this.omNetNoResultWrapper.style.display = 'block';
    }
  }

  private renderResultToDOMTemplate(res: OmnetProvider) {
    const newItemTemplate = this.omNetResultItemTemplate.cloneNode(true) as HTMLElement;
    // Update name
    const theName = newItemTemplate.querySelector('.find-omnet-results-name') as HTMLElement;
    theName.textContent = res.Name;

    // Update Address
    const theAddress = newItemTemplate.querySelector('.find-omnet-results-address') as HTMLElement;
    theAddress.textContent = res.VendorAddress.Address1;

    // Update phone number/ fax
    const theFax = newItemTemplate.querySelector('.find-omnet-results-fax') as HTMLElement;
    if (res.VendorPhones[1].ContactValue) {
      theFax.innerHTML = res.VendorPhones[1].ContactValue + '<br>' + res.VendorPhones[0].ContactValue;
    } else theFax.textContent = res.VendorPhones[0].ContactValue;

    // Update Service Area
    const theServiceArea = newItemTemplate.querySelector('.find-omnet-results-service-area') as HTMLElement;
    theServiceArea.textContent = res.ServiceArea;

    // Update specialty
    const theSpecialty = newItemTemplate.querySelector('.find-omnet-results-specialty') as HTMLElement;
    theSpecialty.textContent = res.CodeDescription;

    // Append to list wrapper
    this.omNetResultWrapper.append(newItemTemplate);
  }
}
